import { useMemo } from 'react';
import { useEntitySecurityEvaluator } from '../../../auth/Security';
import { useI18n } from '../../../i18n/I18n';
import { useDataLegacyRowPermissions } from '../../hooks/useDataLegacyRowPermissions';
import { useDataRowPermissions } from '../../hooks/useDataRowPermissions';
import { getDataGridActions } from '../getDataGridActions';
import type { UseDataGridActionsParams } from '../types';

export function useDataGridActions({
    params,
    feature,
    entityName,
    onActivateOrInactivateHandle,
    rowDetails,
    editHandlers,
    onShare,
    deleteHandlers,
    copyHandlers,
    customActions,
    customRowActions = [],
    isMobile,
    actionLabels,
    hideEditActionWhenInlineEditing = false
}: UseDataGridActionsParams) {
    const t = useI18n();

    const shouldUseCentralizedSecurityFramework = Boolean(entityName);

    const legacyRowPermissions = useDataLegacyRowPermissions(feature, params.row.security);
    const centralizedRowPermissions = useDataRowPermissions(entityName, params.row.security);
    const resolvedRowPermissions = shouldUseCentralizedSecurityFramework
        ? centralizedRowPermissions
        : legacyRowPermissions;

    const evaluateSecurity = useEntitySecurityEvaluator();

    return useMemo(
        () =>
            getDataGridActions({
                entityName,
                params,
                resolvedRowPermissions,
                evaluateSecurity,
                shouldUseCentralizedSecurityFramework,
                onActivateOrInactivateHandle,
                rowDetails,
                editHandlers,
                onShare,
                deleteHandlers,
                copyHandlers,
                customActions,
                customRowActions,
                isMobile,
                translateLabelToken: t,
                actionLabels,
                hideEditActionWhenInlineEditing
            }),
        [
            entityName,
            params,
            resolvedRowPermissions,
            evaluateSecurity,
            shouldUseCentralizedSecurityFramework,
            onActivateOrInactivateHandle,
            rowDetails,
            editHandlers,
            onShare,
            deleteHandlers,
            copyHandlers,
            customActions,
            customRowActions,
            isMobile,
            t,
            actionLabels,
            hideEditActionWhenInlineEditing
        ]
    );
}
